import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import { useCallback } from 'react';
import useEventListener from '../../../../../hooks/useEventListener';
import { useIsMobileView } from '../../../../../hooks/useMediaQuery';
import { ProductType } from '../../../../../models/product/ProductType';
import { Button, Card, Icon } from '../../../../shared';
import { SimpleSummary, SimpleSummaryProps } from '../simple-summary/SimpleSummary';
import SimpleTotal from '../simple-total/SimpleTotal';

import type { SelectableItem } from '~/models/CampaignModel';
import './expandable-basket-test-version.scss';

type Props = SimpleSummaryProps & {
  selectedProducts: SelectableItem[];
  isOpen: boolean;
  toggleSummary: () => void;
};
export const ExpandableBasket: React.FC<Props> = ({ selectedProducts, isOpen, toggleSummary, hideShipping }) => {
  const isMobileView = useIsMobileView();

  const activelyChosenProducts = selectedProducts.filter(
    (product) =>
      product &&
      product.productType !== ProductType.Shipping &&
      !(product.productType === ProductType.Installation && product.pricing.price === 0),
  );

  const hasPremiums = selectedProducts.filter((product) => product.productType === ProductType.Premium).length > 0;
  const name = activelyChosenProducts.length > 0 ? activelyChosenProducts[0].displayData.name : null;

  const parentDivClass = classNames('expandable-basket-test-version', { open: isOpen });
  const summaryContainer = classNames('summary-container', { dual: !isMobileView });
  const premiumAdded = classNames({ 'premium-added': hasPremiums });

  const parentDivAriaLabel = `Handlekurv, ${activelyChosenProducts.length} ${activelyChosenProducts.length === 1 ? 'vare' : 'varer'}`;
  const itemCounterAriaLabel = `${activelyChosenProducts.length} ${activelyChosenProducts.length === 1 ? 'vare' : 'varer'} i handlekurven`;
  const toggleButtonAriaLabel = isOpen ? 'Lukk detaljer for handlekurv' : 'Vis detaljer for handlekurv';

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        toggleSummary();
      }
    },
    [toggleSummary],
  );

  useEventListener('keydown', onKeyDown, isOpen);

  const productNames = activelyChosenProducts.map((p) => p.displayData.name).join(', ');

  return (
    <FocusTrap active={isOpen}>
      <aside data-testid="expandable-basket" data-products={productNames} className={parentDivClass} aria-label={parentDivAriaLabel} tabIndex={-1}>
        <button className="overlay" onClick={toggleSummary}></button>

        <div className="basket">
          <div className="head">
            <div className="head-container">
              <div className="head-inner-container">
                <div className="basket-content">
                  {isMobileView && name ? (
                    <h2 className="subscription-text button-or-bold">{name}</h2>
                  ) : (
                    <h2 className="title tag">Min bestilling</h2>
                  )}

                  <div className="flex-container">
                    {!isOpen && (
                      <div className={`icon-and-counter ${premiumAdded}`}>
                        <Icon type="cart" fill="white" size={1.8} />

                        <div className="item-counter button-or-bold" aria-live="polite" aria-atomic="true">
                          <span className="sr-only">{itemCounterAriaLabel}</span>
                          <span aria-hidden>{activelyChosenProducts.length}</span>
                        </div>
                      </div>
                    )}

                    {!isMobileView && name && <h3 className="subscription-text button-or-bold">{name}</h3>}
                  </div>
                </div>

                <Button
                  type="secondary"
                  iconMargin="left"
                  aria-label={toggleButtonAriaLabel}
                  aria-expanded={isOpen}
                  onClick={toggleSummary}
                >
                  {isOpen && isMobileView && <p>Lukk</p>}
                  {isOpen && !isMobileView && <p>Lukk detaljer</p>}
                  {!isOpen && isMobileView && <p>Vis mer</p>}
                  {!isOpen && !isMobileView && <p>Vis detaljer</p>}
                  {!isMobileView && (
                    <Icon type="chevron" fill="white" size={isMobileView ? 1.5 : 2} flip={isOpen ? undefined : 'vertical'} />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {isOpen && (
            <div className="expandable" aria-hidden={!isOpen}>
              <div className={summaryContainer}>
                <Card className="card-left">
                  <SimpleSummary onlyActivelyChosenProducts={true} selectedProducts={selectedProducts} hideShipping={hideShipping} />
                </Card>
                <Card className={premiumAdded} color="gray-dark">
                  <SimpleTotal selectedProducts={selectedProducts} />
                </Card>
              </div>
            </div>
          )}
        </div>
      </aside>
    </FocusTrap>
  );
};
